import { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../../hooks/useProjects';
import { useRoutedTasks, useOverrideTaskRouting } from '../../hooks/useBotAdmin';
import { formatDisplayDateTime } from '../../utils/format';
import { HeldRobotTasksSection } from './HeldRobotTasksSection';
import type { RoutedTask } from '../../services/api/botAdmin';

/**
 * Phase 112 (Plan 112-04 / PCROUTE-06, D-112-06) — the operator escape
 * hatch: per-project routing read + audited override, a sibling card to
 * `ProjectQueueConfigForm` on the Bot Admin page's Connections tab (DP-10).
 *
 * Non-scope, per D-112-09: no "waiting on human" state, no question
 * rendering, no answer field — this panel only SHOWS executor + provenance
 * and lets an admin change the executor. A HITL bridge belongs to Phase 115
 * (PCOPS-05), not here.
 */

/**
 * Renders the three provenances (advisory, fallback, override) as visually
 * distinct badges — tellable apart at a glance without opening the audit
 * table (the manual verification PCROUTE-06 records in `112-VALIDATION.md`).
 * `null` means the routing request is open but not yet decided.
 */
function ProvenanceBadge({ source }: { source: RoutedTask['source'] }) {
  if (source === 'advisory') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
        Advised
      </span>
    );
  }
  if (source === 'fallback') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
        Fallback
      </span>
    );
  }
  if (source === 'override') {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
        Overridden
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      Pending
    </span>
  );
}

export function RoutingOverridePanel() {
  const { data: projects } = useProjects();
  const [projectId, setProjectId] = useState('');
  const selectedProjectId = projectId ? Number(projectId) : undefined;

  const { data: routedTasks, isLoading, error } = useRoutedTasks(selectedProjectId);
  const overrideMutation = useOverrideTaskRouting(selectedProjectId);
  const [overridingTaskId, setOverridingTaskId] = useState<number | null>(null);

  // Only active (in_progress) projects, sorted by name — same convention as
  // ProjectQueueConfigForm's project selector.
  const projectOptions = (projects ?? [])
    .filter((p) => p.state === 'in_progress')
    .sort((a, b) => a.name.localeCompare(b.name));

  const rows = routedTasks ?? [];

  // Factory™ is the only executor; a task routed anywhere else (a legacy
  // Monka decision) can only be moved onto it.
  const handleOverride = (row: RoutedTask) => {
    setOverridingTaskId(row.taskId);
    overrideMutation.mutate(
      { taskId: row.taskId, input: { executor: 'paperclip' } },
      { onSettled: () => setOverridingTaskId(null) }
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-sm font-semibold text-gray-900">Routing overrides</p>
      <p className="text-sm text-gray-500 mt-0.5">
        See how each routed task was decided, and move a task that is not on Factory™ onto it.
        Overriding an already-claimed task applies to its next dispatch — it does not cancel a run
        in flight.
      </p>

      <div className="mt-3">
        <label
          htmlFor="routing-override-project"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Project
        </label>
        <select
          id="routing-override-project"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Select a project…</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.slug})
            </option>
          ))}
        </select>
      </div>

      {overrideMutation.isError && (
        <p className="mt-2 text-sm text-red-600">{(overrideMutation.error as Error).message}</p>
      )}

      {selectedProjectId ? <HeldRobotTasksSection projectId={selectedProjectId} /> : null}

      {!selectedProjectId ? null : isLoading ? (
        <p className="mt-3 text-sm text-gray-400">Loading routed tasks…</p>
      ) : error ? (
        <p className="mt-3 text-sm text-red-600">Failed to load routed tasks.</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No routed tasks on this project yet.</p>
      ) : (
        <div className="mt-4 overflow-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Task
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Provenance
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Decided
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row, idx) => (
                <tr key={row.taskId} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-4 py-2 text-sm whitespace-nowrap">
                    {row.code ? (
                      <span className="font-medium text-gray-900">{row.code}</span>
                    ) : null}
                    <span className="text-gray-700"> {row.name}</span>
                  </td>
                  <td className="px-4 py-2 text-sm whitespace-nowrap">
                    <ProvenanceBadge source={row.source} />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {formatDisplayDateTime(row.decidedAt)}
                    {/* DP-11 — a claimed task's override applies to the NEXT
                        dispatch; it never cancels the run already in flight. */}
                    {row.botClaimedAt ? (
                      <p className="mt-0.5 text-xs text-amber-600">
                        Claimed by the bot — an override here applies to the next dispatch.
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {row.executor === 'paperclip' ? null : (
                      <button
                        type="button"
                        onClick={() => handleOverride(row)}
                        disabled={overridingTaskId === row.taskId}
                        aria-label={`Route ${row.code ?? row.name} to Factory™`}
                        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 disabled:opacity-40 disabled:hover:text-gray-500"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                        Route to Factory™
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
